/**
 * Konfigurasi Akses Menu Pengembalian / Return per Toko
 * Mendukung kombinasi:
 * 1. Dinamis dari API settings backend (prioritas utama)
 * 2. Cache di localStorage (prioritas kedua jika offline / setelah diubah di Pengaturan Sistem)
 * 3. Default Fallback statis frontend (prioritas ketiga / safety net)
 */

// LocalStorage key untuk cache pengaturan akses refund
export const REFUND_SETTINGS_STORAGE_KEY = 'komala_refund_allowed_cabang_ids';

// Fallback default: Daftar ID atau nama cabang yang diizinkan mengakses pengembalian
// Jika di backend belum disetel sama sekali, nilai default ini yang akan digunakan.
export const DEFAULT_REFUND_ALLOWED_CABANG_IDS = ['1', '2'];

/**
 * Parsing nilai daftar cabang menjadi array string ID/nama yang bersih
 * @param {string|Array} rawValue 
 * @returns {string[]}
 */
export function parseAllowedCabangIds(rawValue) {
  if (!rawValue && rawValue !== 0) return [];
  if (Array.isArray(rawValue)) {
    return rawValue.map((item) => String(item).trim()).filter(Boolean);
  }
  return String(rawValue)
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

/**
 * Mengambil daftar cabang yang diizinkan berdasarkan hirarki prioritas
 * @param {Object} settings - Object settings dari MasterDataContext / API
 * @returns {string[]}
 */
export function getAllowedRefundCabangIds(settings = null) {
  // 1. Prioritas 1: Dari backend settings
  if (settings && (settings.refund_allowed_cabang_ids !== undefined && settings.refund_allowed_cabang_ids !== null)) {
    const fromSettings = parseAllowedCabangIds(settings.refund_allowed_cabang_ids);
    return fromSettings;
  }

  // 2. Prioritas 2: Dari LocalStorage cache
  if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
    try {
      const cached = localStorage.getItem(REFUND_SETTINGS_STORAGE_KEY);
      if (cached !== null && cached !== undefined) {
        return parseAllowedCabangIds(cached);
      }
    } catch (e) {
      console.warn('Gagal membaca cache refund_allowed_cabang_ids:', e);
    }
  }

  // 3. Prioritas 3 (Fallback Opsi 3): Nilai default statis frontend
  return DEFAULT_REFUND_ALLOWED_CABANG_IDS;
}

/**
 * Memeriksa apakah toko / user tertentu diizinkan mengakses menu Pengembalian
 * @param {Object} params
 * @param {string|number} params.cabangId - ID Cabang user saat ini
 * @param {string} [params.cabangName] - Nama Cabang user saat ini
 * @param {string} [params.role] - Role user (owner/admin bypass)
 * @param {Object} [params.settings] - Settings master data
 * @returns {boolean}
 */
export function isRefundAllowedForCabang({
  cabangId,
  cabangName = '',
  role = '',
  settings = null,
}) {
  const normalizedRole = String(role || '').toLowerCase().trim();

  // Hanya Owner dan Admin Pusat (tanpa cabang tertentu) yang bypass akses
  // Jika user memiliki cabangId (seperti kasir toko), akses harus mengikuti izin toko tersebut
  if (!cabangId && (normalizedRole === 'owner' || normalizedRole === 'admin')) {
    return true;
  }

  const allowedIds = getAllowedRefundCabangIds(settings);

  // Jika allowedIds kosong (misal semua toko dinonaktifkan secara sengaja)
  if (!allowedIds || allowedIds.length === 0) {
    return false;
  }

  // Jika terdapat wildcard '*' maka semua toko diizinkan
  if (allowedIds.includes('*')) {
    return true;
  }

  const strCabangId = String(cabangId || '').trim();
  const strCabangName = String(cabangName || '').toLowerCase().trim();

  // Cek kecocokan berdasarkan ID cabang
  if (strCabangId && allowedIds.some((id) => String(id).trim() === strCabangId)) {
    return true;
  }

  // Cek kecocokan toleran berdasarkan nama cabang jika ID tidak cocok
  if (strCabangName && allowedIds.some((id) => String(id).toLowerCase().trim() === strCabangName)) {
    return true;
  }

  return false;
}
